import { Dayjs } from "dayjs";
import { number, setLocale, string } from "yup";
import { isDateBeforeNow } from "./dateHelper";

setLocale({
    mixed: {
        default: "is invalid",
        required: "is required",
    },
    string: {
        email: 'must be in "someone@example.com" format',
    },
    number: {
        positive: "must be greater than zero",
        moreThan: "must be greater than ${more}",
    },
});

export const isValidUrl = (urlString: string, isProtocolOptional = false): boolean => {
    // Add protocol if it's optional and not present
    if (isProtocolOptional && !urlString.match(/^http/)) {
        urlString = `http://${urlString}`;
    }

    try {
        const urlObject = new URL(urlString);
        // Regular expression to match valid TLDs (e.g., .com, .org, .net, etc.)
        // Ensures URL has a domain with a dot followed by at least two characters
        const hasTLD = urlObject.hostname.match(/\.[a-z]{2,}$/i);
        return !!hasTLD;
    } catch {
        return false;
    }
};

export const requiredString = (message?: string) => string().trim().required(message);
export const requiredStringButNullable = (message?: string) => string().trim().required(message).nullable();
export const nullableString = () => string().nullable();

export const url = (message = "has an invalid url format") =>
    string()
        .trim()
        .nullable()
        .test("url", message, (value) => {
            return value?.trim().length ? isValidUrl(value) : true;
        });

export const urlWithOptionalProtocol = (message = "has an invalid url format") =>
    string()
        .trim()
        .nullable()
        .test("urlWithOptionalProtocol", message, (value) => {
            return value?.trim().length ? isValidUrl(value, true) : true;
        });

export const requiredEmail = (emailMessage?: string, requiredMessage?: string) => {
    return string()
        .trim()
        .email(emailMessage)
        .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)
        .required(requiredMessage);
};

export const nullableEmail = (emailMessage?: string, requiredMessage?: string) => {
    return string()
        .trim()
        .email(emailMessage)
        .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)
        .nullable();
};

export const requiredNumber = (typeMessage = "must be a number", requiredMessage?: string) => {
    return number().typeError(typeMessage).required(requiredMessage);
};
export const nullableNumber = () => number().nullable();

export const nullableNumberAtleastZero = (minMessage = "must be greater than or equal to 0") => {
    return number().nullable().min(0, minMessage);
};

export const integerAtleastZero = (
    minMessage = "must be greater than or equal to 0",
    integerMessage = "Cannot use decimal values",
) => {
    return number().min(0, minMessage).integer(integerMessage);
};

export const requiredNumberMoreThanZero = (typeMessage = "must be a number", requiredMessage?: string) =>
    requiredNumber(typeMessage, requiredMessage).moreThan(0);

export const requiredNumberAtleastZero = (
    minMessage = "must be greater than or equal to 0",
    typeMessage = "must be a number",
    requiredMessage?: string,
) => {
    return requiredNumber(typeMessage, requiredMessage).min(0, minMessage);
};

export const requiredIntegerMoreThanZero = (
    typeMessage = "must be a number",
    requiredMessage?: string,
    integerMessage = "Cannot use decimal values",
) => {
    return requiredNumber(typeMessage, requiredMessage).moreThan(0).integer(integerMessage);
};

export const requiredNumberButNotZero = (message = "should not be equal to 0") => {
    return number()
        .required()
        .test("nonZero", message, (value) => {
            return Number(value) !== 0;
        });
};

/** Verifies if the "value" is not before the `compareTo` date */
export const daysCantBeInThePast = (compareTo: Dayjs, message = "date cannot be in the past") => {
    return integerAtleastZero().test("canNotBeInThePast", message, (value) => {
        return !isDateBeforeNow(compareTo, value ?? 0);
    });
};

export const requiredNumberNotEquals = (compareTo: number, message = "should not be equal") => {
    return requiredNumber().test("notEqualsTheAmount", message, (value) => value! !== compareTo);
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isValidEmail = (email: string): boolean => {
    return EMAIL_REGEX.test(email);
};
